import { useState } from 'react'
import { useTranslation } from 'next-i18next'

import { countryData } from '~/shared/constants/locale.const'

import styles from '~/shared/styles/Input.module.css'

import SVGImage from './Image/SVGImage'
import { FieldError } from './FieldError'
import SVGIcon from './Icons/svgs/SVGIcon'
import ChevronDownIcon from './Icons/svgs/ChevronDownIcon'

export interface ErrorMessage {
  message: string
}

interface Props<T> {
  autoComplete: string
  className?: string
  countryCode: string
  disabled?: boolean
  fieldErrors?: string
  label: string
  name: string
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  setCountryCode: (code: string) => void
  setValue: (val: T) => void
  type: string
  value: string
}

export function InputPhoneNumber<T extends string>(props: Props<T>): React.ReactElement {
  const {
    autoComplete,
    className,
    countryCode,
    disabled,
    fieldErrors,
    label,
    name,
    onClick,
    setCountryCode,
    setValue,
    type,
    value,
  } = props

  const { t } = useTranslation('generics')

  const [focused, setFocused] = useState(false)

  const handleOnBlur = (): void => {
    setFocused(false)
  }

  const handleOnFocus = (): void => {
    setFocused(true)
  }

  return (
    <div className={`${styles['form-input-wrapper']}`}>
      <div className="flex space-x-2">
        <div className="relative flex items-center rounded bg-white">
          <div className="w-max space-x-2.5 px-4 flex items-center pointer-events-none">
            {countryCode && (
              <SVGImage
                alt={t('countryCode')}
                imageClassName="h-6 rounded-0.5"
                srcKey={`country-flags-rect/${countryCode}.svg`}
              />
            )}
            <span className="shrink-0">
              <SVGIcon icon={ChevronDownIcon} pathClassName="text-black" size={14} />
            </span>
          </div>

          <select
            className="absolute h-full w-full bg-transparent text-transparent focus:outline-none"
            onChange={(event) => setCountryCode(event.target.value)}
            value={countryCode}
          >
            {countryData.map(({ callingCode, code, name: countryName }) => (
              <option className="text-black" key={`${code} ${countryName}`} value={code}>
                {countryName} ({callingCode})
              </option>
            ))}
          </select>
        </div>
        <div
          className={`${styles['form-input-container']} w-full ${
            fieldErrors?.length ? `${styles['error']}` : ''
          } ${className}`}
        >
          <input
            autoComplete={autoComplete}
            className={`${styles['form-input']} tracking-wide ${value ? `${styles['--has-value']}` : ''}`}
            disabled={disabled}
            id={name}
            name={name}
            onBlur={handleOnBlur}
            onChange={(e) => setValue(e.target.value as T)}
            onClick={onClick}
            onFocus={handleOnFocus}
            placeholder={(focused && !value) || fieldErrors?.length ? label : ''}
            type={type}
            value={value}
          />

          <label className={`${styles['form-input-label']} ${value ? `${styles['--has-value']}` : ''}`} htmlFor={name}>
            {label}
          </label>
        </div>
      </div>
      <FieldError error={fieldErrors} />
    </div>
  )
}
